import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

import { insertPastedList, isMultiValuePaste } from './filterListInput';

const useStyles = makeStyles((theme) => ({
  filterPanel: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  columnSelect: {
    minWidth: 140,
  },
  valueInput: {
    flex: 1,
    minWidth: 200,
  },
  buttonRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

export interface FilterRow {
  id: string;
  column: string;
  value: string;
}

interface FilterableColumn {
  field: string;
  headerName: string;
}

interface CustomFilterPanelProps {
  filters: FilterRow[];
  onFiltersChange: (filters: FilterRow[]) => void;
  columns?: FilterableColumn[];
}

const DEFAULT_FILTERABLE_COLUMNS: FilterableColumn[] = [
  { field: 'id', headerName: 'Recording ID' },
  { field: 'userEmail', headerName: 'Email' },
  { field: 'userId', headerName: 'User ID' },
  { field: 'comment', headerName: 'Comment' },
  { field: 'fileName', headerName: 'File Name' },
  { field: 'flags', headerName: 'Flags' },
];

// Placeholder hints per filterable column; anything unlisted falls back to the
// generic label. List columns split on commas, spaces and new lines (#575);
// `comment` is free text, so only commas separate its values.
const LIST_HINT = 'comma, space or new line separated';

export const FILTER_PLACEHOLDERS: Record<string, string> = {
  comment: 'Comments, comma separated',
  fileName: `File names, ${LIST_HINT}`,
  flags: `Flags, ${LIST_HINT}`,
  id: `IDs, ${LIST_HINT}`,
  status: `Statuses, ${LIST_HINT}`,
  userEmail: `Emails, ${LIST_HINT}`,
  userId: `User IDs, ${LIST_HINT}`,
};

export function CustomFilterPanel({ filters, onFiltersChange, columns }: CustomFilterPanelProps) {
  const FILTERABLE_COLUMNS = columns || DEFAULT_FILTERABLE_COLUMNS;
  const classes = useStyles();

  const handleAddFilter = () => {
    const newFilter: FilterRow = {
      id: `filter-${Date.now()}`,
      column: 'id',
      value: '',
    };

    onFiltersChange([...filters, newFilter]);
  };

  const handleRemoveFilter = (filterId: string) => {
    onFiltersChange(filters.filter((f) => f.id !== filterId));
  };

  const handleColumnChange = (filterId: string, column: string) => {
    onFiltersChange(
      filters.map((f) => (f.id === filterId ? { ...f, column, value: '' } : f))
    );
  };

  const handleValueChange = (filterId: string, value: string) => {
    onFiltersChange(
      filters.map((f) => (f.id === filterId ? { ...f, value } : f))
    );
  };

  const handleClearAll = () => {
    onFiltersChange([]);
  };

  // A single-line input never keeps the line breaks of a pasted column, and
  // what it does instead is browser-specific (headless Chrome 131: spaces;
  // other browsers/settings strip them, gluing the values together, or keep
  // only the first line). Paste multi-line/tabbed text as ", "-separated
  // values at the cursor instead; anything else pastes natively.
  const handlePaste = (filter: FilterRow, event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData?.getData('text/plain') || event.clipboardData?.getData('text');

    if (!text || !isMultiValuePaste(text)) return;

    event.preventDefault();

    const input = event.currentTarget;
    const start = input.selectionStart ?? filter.value.length;
    const end = input.selectionEnd ?? start;
    const { value, caret } = insertPastedList(filter.value, start, end, text);

    handleValueChange(filter.id, value);
    // The controlled value re-renders after this handler; restore the caret then.
    setTimeout(() => {
      if (document.activeElement === input) input.setSelectionRange(caret, caret);
    }, 0);
  };

  return (
    <Box className={classes.filterPanel}>
      {filters.map((filter) => (
        <Box key={filter.id} className={classes.filterRow}>
          <FormControl variant="outlined" size="small" className={classes.columnSelect}>
            <InputLabel>Column</InputLabel>
            <Select
              value={filter.column}
              label="Column"
              onChange={(e) => handleColumnChange(filter.id, e.target.value as string)}
            >
              {FILTERABLE_COLUMNS.map((col) => (
                <MenuItem key={col.field} value={col.field}>
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            size="small"
            className={classes.valueInput}
            inputProps={{
              'aria-label': 'Filter value',
              onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(filter, e),
            }}
            placeholder={FILTER_PLACEHOLDERS[filter.column] ?? 'Filter value'}
            value={filter.value}
            onChange={(e) => handleValueChange(filter.id, e.target.value)}
          />

          <IconButton size="small" onClick={() => handleRemoveFilter(filter.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}

      <Box className={classes.buttonRow}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddFilter}
        >
          Add Filter
        </Button>
        {filters.length > 0 && (
          <Button variant="outlined" size="small" onClick={handleClearAll}>
            Clear All
          </Button>
        )}
      </Box>
    </Box>
  );
}
